import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import {
  acceptAllViaCookieYes,
  grantTrackingConsent,
  hasTrackingConsent,
  isCookieBannerElement,
  isCookieYesBannerVisible,
  isCookieYesConsentCompleted,
  isCookieYesLoaded,
  revokeTrackingConsent,
} from 'utils/cookieYesConsent'

const NON_NAVIGATION_SCHEMES = new Set(['javascript', 'data', 'vbscript', 'mailto', 'tel'])

const isNonNavigationHref = (href: string): boolean => {
  const scheme = href.split(':')[0].trim().toLowerCase()
  return href.includes(':') && NON_NAVIGATION_SCHEMES.has(scheme)
}

/**
 * Next <Link> geçişleri routeChangeStart ile yakalanır; bu kontrol router'a girmeyen sert
 * navigasyonları (diğer ürün sayfaları, dış linkler, target="_blank", düz <a>) kapsar.
 */
export const isNavigatingLinkClick = (event: MouseEvent): boolean => {
  if (event.defaultPrevented) return false
  if (event.button !== 0) return false
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false

  const { target } = event
  if (!(target instanceof Element)) return false
  if (isCookieBannerElement(target)) return false

  const href = target.closest('a')?.getAttribute('href')
  if (!href || href === '#' || href.startsWith('#')) return false

  return !isNonNavigationHref(href)
}

/**
 * CookieYes davranış katmanı (yalnızca CookieYes aktif host'larda `enabled=true`).
 *
 * 1) Örtülü onay: OneTrust yapılandırmasındaki `NextPageAcceptAllCookies` ile parite.
 *    Banner gösterilmişken kullanıcı bir sayfaya gidiyorsa (route change, sert link tıklaması,
 *    form submit, tarayıcı geri/ileri) "tümünü kabul et" tetiklenir. Banner hiç yüklenmemişse
 *    veya kullanıcı zaten karar vermişse hiçbir şey yapılmaz.
 *
 * 2) Google Consent Mode senkronu: CookieYes yüklenince/karar güncellenince
 *    - karar yok → granted (örtülü model; CookieYes'in kendi "denied" güncellemesi geri alınır),
 *    - tracking kabul → granted, tüm tracking ret → denied.
 */
const useCookieYesConsent = (enabled: boolean): void => {
  const router = useRouter()
  const handledRef = useRef(false)

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined

    handledRef.current = false
    let bannerReady = false

    const syncGoogleConsent = (): void => {
      if (!isCookieYesConsentCompleted()) {
        grantTrackingConsent()
        return
      }
      if (hasTrackingConsent()) {
        grantTrackingConsent()
        return
      }
      revokeTrackingConsent()
    }

    const handleBannerLoad = (): void => {
      bannerReady = true
      if (isCookieYesConsentCompleted()) handledRef.current = true
      syncGoogleConsent()
    }

    const handleConsentUpdate = (): void => {
      if (isCookieYesConsentCompleted()) handledRef.current = true
      syncGoogleConsent()
    }

    const implicitlyAccept = (): void => {
      if (handledRef.current) return
      if (isCookieYesConsentCompleted()) {
        handledRef.current = true
        return
      }
      if (!bannerReady || !isCookieYesBannerVisible()) return

      handledRef.current = acceptAllViaCookieYes()
    }

    const handleSubmit = (event: Event): void => {
      if (!(event.target instanceof HTMLFormElement)) return
      implicitlyAccept()
    }

    const handleLinkClick = (event: MouseEvent): void => {
      if (handledRef.current) return
      if (!isNavigatingLinkClick(event)) return
      implicitlyAccept()
    }

    const handleRouteChange = (): void => implicitlyAccept()
    const handlePopState = (): void => implicitlyAccept()

    document.addEventListener('cookieyes_banner_load', handleBannerLoad)
    document.addEventListener('cookieyes_consent_update', handleConsentUpdate)
    document.addEventListener('submit', handleSubmit, { capture: true })
    document.addEventListener('click', handleLinkClick, { capture: true })
    window.addEventListener('popstate', handlePopState)
    router.events.on('routeChangeStart', handleRouteChange)

    // Hook, CookieYes zaten yüklendikten sonra bağlanmış olabilir.
    if (isCookieYesLoaded()) handleBannerLoad()

    return () => {
      document.removeEventListener('cookieyes_banner_load', handleBannerLoad)
      document.removeEventListener('cookieyes_consent_update', handleConsentUpdate)
      document.removeEventListener('submit', handleSubmit, { capture: true })
      document.removeEventListener('click', handleLinkClick, { capture: true })
      window.removeEventListener('popstate', handlePopState)
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [enabled, router.events])
}

export default useCookieYesConsent
